/**
 * ADMIN MODULE — LinkedIn Flywheel data access.
 * Whitelisted reads/writes over the flywheel tables (RLS pins them to platform
 * admins). Components call these instead of touching supabase directly, mirroring
 * the app's service-layer pattern. Errors are logged here and surfaced generic.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";

export const POST_STATUSES = ["draft", "approved", "queued", "published"];
export const POST_TYPES_ALL = ["pain_story", "education", "social_proof", "hot_take"];

function fail(where, error, msg) {
  console.error(`[flywheelService.${where}]`, error);
  throw new Error(msg);
}

// ---- Panel 1: Post Queue ----
export async function listPosts() {
  const { data, error } = await supabase
    .from("linkedin_posts")
    .select("id, draft_content, post_type, status, scheduled_for, linkedin_post_url, image_url, image_prompt, image_status, created_at")
    .order("created_at", { ascending: false });
  if (error) fail("listPosts", error, "Could not load posts.");
  return data ?? [];
}

export async function setPostStatus(id, status) {
  if (!["draft", "approved", "queued", "published", "rejected", "error"].includes(status)) {
    throw new Error("Invalid status.");
  }
  const { error } = await supabase.from("linkedin_posts").update({ status }).eq("id", id);
  if (error) fail("setPostStatus", error, "Could not update the post.");
}

export async function updatePostContent(id, draft_content) {
  const { error } = await supabase
    .from("linkedin_posts")
    .update({ draft_content: String(draft_content ?? "").slice(0, 8000) })
    .eq("id", id);
  if (error) fail("updatePostContent", error, "Could not save the post.");
}

// Records a human edit as a lesson the writer agent learns from. Best-effort —
// a failed lesson write must never block the edit/approve flow.
export async function recordWritingLesson({ post_id, post_type, original_text, edited_text, note }) {
  const { error } = await supabase.from("writing_lessons").insert({
    post_id: post_id ?? null,
    post_type: post_type ?? null,
    original_text: String(original_text ?? "").slice(0, 8000),
    edited_text: String(edited_text ?? "").slice(0, 8000),
    note: note ? String(note).slice(0, 2000) : null,
  });
  if (error) console.warn("[flywheelService.recordWritingLesson]", error);
}

function validUrlOrThrow(u) {
  const url = String(u ?? "").trim();
  if (url && !/^https?:\/\//i.test(url)) throw new Error("Enter a valid http(s) URL.");
  return url;
}

// Save the live LinkedIn post URL (so the engagement scraper knows what to watch).
export async function setPostUrl(id, linkedin_post_url) {
  const url = validUrlOrThrow(linkedin_post_url);
  const { error } = await supabase
    .from("linkedin_posts")
    .update({ linkedin_post_url: url || null })
    .eq("id", id);
  if (error) fail("setPostUrl", error, "Could not save the URL.");
}

// Mark a post live: status=published, stamp published_at, and (optionally) save its URL.
export async function markPublished(id, linkedin_post_url) {
  const url = validUrlOrThrow(linkedin_post_url);
  const patch = { status: "published", published_at: new Date().toISOString() };
  if (url) patch.linkedin_post_url = url;
  const { error } = await supabase.from("linkedin_posts").update(patch).eq("id", id);
  if (error) fail("markPublished", error, "Could not mark the post published.");
}

// ---- Panel 2: Warm Leads ----
export async function listWarmLeads() {
  const { data, error } = await supabase
    .from("linkedin_engagement")
    .select("id, profile_name, linkedin_profile_url, engagement_count, lead_status, is_warm_lead, comment_text, engagement_type, scraped_at")
    .eq("is_warm_lead", true)
    .order("scraped_at", { ascending: false });
  if (error) fail("listWarmLeads", error, "Could not load warm leads.");
  return data ?? [];
}

// Full engagement history for one person (all rows sharing their profile URL).
export async function listEngagementForProfile(profileUrl) {
  const { data, error } = await supabase
    .from("linkedin_engagement")
    .select("id, post_id, engagement_type, comment_text, engagement_count, lead_status, scraped_at")
    .eq("linkedin_profile_url", profileUrl)
    .order("scraped_at", { ascending: false });
  if (error) fail("listEngagementForProfile", error, "Could not load engagement history.");
  return data ?? [];
}

// ---- Panel 3: Comment Queue ----
export async function listPendingComments() {
  const { data, error } = await supabase
    .from("comment_queue")
    .select("id, engagement_id, original_post_text, their_comment, draft_reply, status, created_at")
    .eq("status", "pending")
    .order("created_at", { ascending: false });
  if (error) fail("listPendingComments", error, "Could not load the comment queue.");
  return data ?? [];
}

// Approve copies the (possibly edited) reply into approved_reply and flips status.
export async function approveComment(id, approvedReply) {
  const { error } = await supabase
    .from("comment_queue")
    .update({ status: "approved", approved_reply: String(approvedReply ?? "").slice(0, 5000) })
    .eq("id", id);
  if (error) fail("approveComment", error, "Could not approve the reply.");
}

export async function skipComment(id) {
  const { error } = await supabase.from("comment_queue").update({ status: "rejected" }).eq("id", id);
  if (error) fail("skipComment", error, "Could not skip the reply.");
}

// ---- Panel 4: CTA Status ----
export async function getActiveCta() {
  const { data, error } = await supabase
    .from("conversion_config")
    .select("id, launch_stage, cta_text, cta_url, active, updated_at")
    .eq("active", true)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) fail("getActiveCta", error, "Could not load the CTA.");
  return data ?? null;
}

export async function countConverted() {
  const { count, error } = await supabase
    .from("linkedin_engagement")
    .select("*", { count: "exact", head: true })
    .eq("converted", true);
  if (error) { console.error("[flywheelService.countConverted]", error); return null; }
  return count ?? 0;
}

// ---- Flywheel Settings (posts/week, user-managed post types, cadence schedule) ----
// type_defs is the source of truth for post types: [{key,label,text_brief,image_brief,enabled}].
// On save we DERIVE the legacy post_types (enabled keys) + type_briefs ({key:text}) so the Python
// writer keeps working unchanged; image_brief is the new per-type direction the Image Designer reads.

const DEFAULT_TYPE_DEFS = [
  { key: "pain_story",   label: "Pain Story",   text_brief: "", image_brief: "", enabled: true },
  { key: "education",    label: "Education",    text_brief: "", image_brief: "", enabled: true },
  { key: "social_proof", label: "Social Proof", text_brief: "", image_brief: "", enabled: true },
  { key: "hot_take",     label: "Hot Take",     text_brief: "", image_brief: "", enabled: true },
];

export function slugifyTypeKey(label) {
  return String(label || "").toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || "type";
}

// Build a normalized type_defs array from a settings row (handles legacy rows with no type_defs).
function normalizeTypeDefs(row) {
  const raw = Array.isArray(row?.type_defs) ? row.type_defs : null;
  if (raw && raw.length) {
    return raw.map((d) => ({
      key: slugifyTypeKey(d.key || d.label),
      label: String(d.label || d.key || "Type").slice(0, 60),
      text_brief: typeof d.text_brief === "string" ? d.text_brief.slice(0, 2000) : "",
      image_brief: typeof d.image_brief === "string" ? d.image_brief.slice(0, 2000) : "",
      enabled: d.enabled !== false,
    }));
  }
  // Seed from the legacy post_types + type_briefs (+ built-in labels) the first time.
  const briefs = row?.type_briefs || {};
  const active = new Set(row?.post_types || DEFAULT_TYPE_DEFS.map((d) => d.key));
  return DEFAULT_TYPE_DEFS.map((d) => ({
    ...d,
    text_brief: typeof briefs[d.key] === "string" ? briefs[d.key] : "",
    enabled: active.has(d.key),
  }));
}

export async function getSettings() {
  const { data, error } = await supabase
    .from("flywheel_settings")
    .select("id, posts_per_week, post_types, schedule, auto_generate, timezone, type_briefs, type_defs, updated_at")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) fail("getSettings", error, "Could not load settings.");
  if (data) data.type_defs = normalizeTypeDefs(data);
  return data ?? null;
}

const SETTINGS_FIELDS = new Set(["posts_per_week", "post_types", "schedule", "auto_generate", "timezone", "type_briefs", "type_defs"]);

export async function updateSettings(id, patch) {
  const out = {};
  for (const k of SETTINGS_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(patch, k)) out[k] = patch[k];
  }
  if ("posts_per_week" in out) {
    out.posts_per_week = Math.max(1, Math.min(21, Number(out.posts_per_week) || 1));
  }

  // type_defs = source of truth. Sanitize it, then derive post_types + type_briefs.
  let validKeys = null;
  if ("type_defs" in out) {
    const seen = new Set();
    const defs = (Array.isArray(out.type_defs) ? out.type_defs : [])
      .map((d) => {
        let key = slugifyTypeKey(d.key || d.label);
        while (seen.has(key)) key += "_2";
        seen.add(key);
        return {
          key,
          label: String(d.label || key).slice(0, 60).trim() || key,
          text_brief: typeof d.text_brief === "string" ? d.text_brief.slice(0, 2000) : "",
          image_brief: typeof d.image_brief === "string" ? d.image_brief.slice(0, 2000) : "",
          enabled: d.enabled !== false,
        };
      })
      .filter((d) => d.label);
    if (defs.length === 0) throw new Error("Add at least one post type.");
    if (!defs.some((d) => d.enabled)) throw new Error("Enable at least one post type.");
    out.type_defs = defs;
    out.post_types = defs.filter((d) => d.enabled).map((d) => d.key);                 // derived
    out.type_briefs = Object.fromEntries(defs.map((d) => [d.key, d.text_brief]));      // derived
    validKeys = new Set(defs.map((d) => d.key));
  } else if ("post_types" in out) {
    out.post_types = (out.post_types || []).filter((t) => POST_TYPES_ALL.includes(t));
    if (out.post_types.length === 0) throw new Error("Pick at least one post type.");
  }

  if ("schedule" in out) {
    // sanitize slots: {day 0-6, time "HH:MM", post_type null|known-key}
    out.schedule = (Array.isArray(out.schedule) ? out.schedule : []).slice(0, 21).map((s) => ({
      day: Math.max(0, Math.min(6, Number(s.day) || 0)),
      time: /^\d{2}:\d{2}$/.test(s.time || "") ? s.time : "09:00",
      post_type: (validKeys ? validKeys.has(s.post_type) : POST_TYPES_ALL.includes(s.post_type)) ? s.post_type : null,
    }));
  }

  // Only honor a standalone type_briefs patch when type_defs wasn't provided (else it's derived above).
  if ("type_briefs" in out && !("type_defs" in patch)) {
    const briefs = {};
    for (const [k, v] of Object.entries(out.type_briefs || {})) {
      if (typeof v === "string") briefs[slugifyTypeKey(k)] = v.slice(0, 2000);
    }
    out.type_briefs = briefs;
  }

  if (Object.keys(out).length === 0) return;
  const { error } = await supabase.from("flywheel_settings").update(out).eq("id", id);
  if (error) fail("updateSettings", error, "Could not save settings.");
}

// Deactivate every row, then insert a new active one (one active CTA at a time).
export async function updateCta({ launch_stage, cta_text, cta_url }) {
  if (!["pre_launch", "live", "new_module"].includes(launch_stage)) {
    throw new Error("Invalid launch stage.");
  }
  const { error: deErr } = await supabase
    .from("conversion_config")
    .update({ active: false })
    .eq("active", true);
  if (deErr) fail("updateCta.deactivate", deErr, "Could not update the CTA.");

  const { error: insErr } = await supabase
    .from("conversion_config")
    .insert({
      launch_stage,
      cta_text: String(cta_text ?? "").slice(0, 500),
      cta_url: String(cta_url ?? "").slice(0, 1000),
      active: true,
    });
  if (insErr) fail("updateCta.insert", insErr, "Could not update the CTA.");
}
